import { useQuery } from '@tanstack/react-query';
import { AxiosError, AxiosInstance } from 'axios';
import { WithdrawalMethodsQueryKeys } from '../../queryKeys';
import {
  UseWithdrawalMethodByIdQueryKey,
  UseWithdrawalMethodsByIdProps,
} from './props';
import { fetchWithdrawalMethodById } from './service';
import { GenericError, WithdrawMethod } from '@starsoft/common/models';
import { useAxiosInstance } from '@starsoft/common/hooks';

/**
 * Custom hook to fetch a specific withdrawal method by ID
 *
 * This hook handles fetching a single withdrawal method's details using its ID.
 * It manages the query state using React Query and considers data stale after 5 seconds.
 * The query will only execute if an ID is provided and the hook is enabled.
 *
 * @param {UseWithdrawalMethodsByIdProps} props - The props object
 * @param {string | number} props.id - The ID of the withdrawal method to fetch
 * @param {boolean} [props.enabled=true] - Whether the query should be enabled
 *
 * @returns {Object} An object containing:
 *   - refetch: Function to manually trigger a refetch
 *   - error: Any error that occurred during fetch
 *   - isLoading: Boolean indicating if fetch is in progress
 *   - withdrawalMethod: The fetched withdrawal method data
 */
export function useWithdrawalMethodById({
  id,
  enabled = true,
}: UseWithdrawalMethodsByIdProps) {
  const axiosInstance: AxiosInstance = useAxiosInstance();
  const queryKey: UseWithdrawalMethodByIdQueryKey = [
    WithdrawalMethodsQueryKeys.ById,
    id as unknown as bigint,
  ];
  const { isPending, error, data, refetch } = useQuery<
    WithdrawMethod,
    AxiosError<GenericError>,
    WithdrawMethod,
    UseWithdrawalMethodByIdQueryKey
  >({
    queryKey,
    queryFn: queryContext =>
      fetchWithdrawalMethodById(queryContext, axiosInstance),
    enabled: !!id && enabled,
    staleTime: 5 * 1000,
  });

  return {
    refetch,
    error: error,
    isLoading: isPending,
    withdrawalMethod: data,
  };
}
